import UploadIcon from '@mui/icons-material/Upload';
import { Alert, Box, Button, DialogActions, DialogContent, Divider, Typography } from '@mui/material';
import DialogTitle from '@mui/material/DialogTitle';
import { Stack } from '@mui/system';
import { isArray } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { Trans } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { applicationSlice } from '../../../redux/ApplicationSlice';
import { projectSlice } from '../../../redux/ProjectSlice';
import { RootState } from '../../../redux/Store';
import checkProjectName from '../../../utils/validation/checkProjectName';
import { Project } from '../../Projects/projects';
import { VisuallyHiddenInput } from '../../VisuallyHiddenInput';
import DuplicateProjectWarning from './DuplicateProjectWarning.js';

interface UploadedFileDialogProps {
  onClose: () => void;
  draggedFiles: Array<File> | File;
  isMultipleFile: boolean;
  open: boolean;
}

const UploadFileDialog = ({ onClose, draggedFiles, isMultipleFile }: UploadedFileDialogProps) => {
  const dispatch = useDispatch();
  const duplicateProjectsArray = useRef<Array<Project>>([]);
  const effectCalled = useRef(false);
  const navigate = useNavigate();

  const { projects } = useSelector((state: RootState) => state.project);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isValidFile, setIsValidFile] = useState(false);

  const [uploadedProject, setUploadedProject] = useState<Array<Project>>();
  const [isMultipleProject, setIsMultipleProject] = useState(false);
  const [openDuplicateProjectWarningDialog, setDuplicateProjectWarningDialog] = useState(false);

  const selectedFileName = selectedFile?.name;

  const handleOpenDuplicateProjectWarningDialog = () => {
    setDuplicateProjectWarningDialog(true);
  };

  const handleCloseDuplicateProjectWarningDialog = () => {
    setDuplicateProjectWarningDialog(false);
    onClose();
  };

  const readFileContent = (file: File) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => resolve(event.target?.result);
      reader.onerror = (error) => reject(error);
      reader.readAsText(file, 'UTF-8');
    });

  const projectExists = (id: string) => projects.some((existingProject: Project) => existingProject.id === id);

  const handleFileUpload = async (uploadedFile: File | Array<File> | null) => {
    const bpsRegex = /(.bps)+/;
    if (!isMultipleFile && uploadedFile && !isArray(uploadedFile)) {
      setIsMultipleProject(false);
      const fileContent = (await readFileContent(uploadedFile)) as string;
      // Do not stamp CURRENT version here — onAdd/onLoad run migrateProject first, then defaults.
      const project = JSON.parse(fileContent) as Project;
      const uploadProject = [project];
      setUploadedProject(uploadProject);
      if (projectExists(project.id)) {
        handleOpenDuplicateProjectWarningDialog();
      } else {
        const name = uploadedFile.name.replace(bpsRegex, '');
        project.name = checkProjectName(name, project.id);
        project.id = self.crypto.randomUUID();
        dispatch(projectSlice.actions.onAddProject(project));
        dispatch(applicationSlice.actions.onOpenDialog({ dialog: null }));
        dispatch(projectSlice.actions.onLoadProject(project));
        window.projectId = project.id;
        onClose();
        navigate(`/editor/${project.id}`);
      }
    } else {
      duplicateProjectsArray.current = [];
      if (!isArray(draggedFiles)) return;
      const filePromises = draggedFiles.map(async (file) => {
        const fileContent = (await readFileContent(file)) as string;
        const project = JSON.parse(fileContent) as Project;
        project.name = file.name.replace(bpsRegex, '');
        return project;
      });

      const multiProjectData = await Promise.all(filePromises);

      multiProjectData.forEach((project: Project) => {
        if (projectExists(project.id)) duplicateProjectsArray.current.push(project);
        else {
          project.id = self.crypto.randomUUID();
          project.name = checkProjectName(project.name, project.id);
          dispatch(projectSlice.actions.onAddProject(project));
        }
      });

      if (duplicateProjectsArray.current.length !== 0) {
        setIsMultipleProject(true);
        setUploadedProject(duplicateProjectsArray.current);
        handleOpenDuplicateProjectWarningDialog();
      }
    }
  };

  useEffect(() => {
    if (!effectCalled.current) {
      effectCalled.current = true; // Prevent duplicate calls in dev mode
      if (draggedFiles) {
        handleFileUpload(draggedFiles);
      }
    }
    // Intentionally omit handleFileUpload: auto-upload should run once for the dropped files, not on every render/state change.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot drag-and-drop upload on dialog open
  }, [draggedFiles]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setIsValidFile(file.name.endsWith('.bps'));
    }
  };

  return (
    <>
      {!draggedFiles && (
        <>
          <DialogTitle variant="sectionTitle" align="center">
            <Trans i18nKey="uploadFileDialog.title">Upload Project</Trans>
          </DialogTitle>
          <Divider />
          <DialogContent style={{ padding: '3rem 2.75rem' }}>
            <Stack direction="column" spacing={2} useFlexGap flexWrap="wrap">
              <Typography variant="body1" fontWeight={500}>
                <Trans i18nKey="uploadFileDialog.fileTypeText">Select a .bps file to open.</Trans>
              </Typography>
              <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" alignItems="center" justifyContent="flex-start">
                <Button component="label" variant="contained" color="primary" size="small" startIcon={<UploadIcon />}>
                  <Trans i18nKey="button.select_file"> Select File</Trans>
                  <VisuallyHiddenInput type="file" onChange={handleFileChange} />
                </Button>
                <Box>
                  {!selectedFile && (
                    <Typography variant="body1">
                      <Trans i18nKey="uploadFileDialog.noFileChosen">No file chosen</Trans>
                    </Typography>
                  )}
                  {isValidFile && selectedFile && (
                    <Typography variant="body1">
                      <Trans i18nKey="uploadFileDialog.selected-file">Selected file: {{ selectedFileName }}</Trans>
                    </Typography>
                  )}
                  {!isValidFile && selectedFile && (
                    <Typography variant="body1">
                      <Trans i18nKey="uploadFileDialog.invalidFileError">Invalid file type. Please select a .bps file.</Trans>
                    </Typography>
                  )}
                </Box>
              </Stack>
              <Alert severity="info">
                <Typography variant="body1">
                  <Trans i18nKey="uploadFileDialog.tooltip">
                    You can upload a project by dragging and dropping them anywhere on the project page!
                  </Trans>
                </Typography>
              </Alert>
            </Stack>
          </DialogContent>
          <Divider />
          <DialogActions style={{ justifyContent: 'center', margin: '2px 0' }}>
            <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
              <Button variant="text" autoFocus onClick={onClose}>
                <Trans i18nKey="button.cancel">Cancel</Trans>
              </Button>
              <Button variant="contained" autoFocus onClick={() => handleFileUpload(selectedFile)} disabled={!isValidFile}>
                <Trans i18nKey="button.confirm">Confirm</Trans>
              </Button>
            </Stack>
          </DialogActions>
        </>
      )}
      {openDuplicateProjectWarningDialog && (
        <DuplicateProjectWarning
          open={openDuplicateProjectWarningDialog}
          onClose={handleCloseDuplicateProjectWarningDialog}
          projects={uploadedProject}
          isMultipleProjects={isMultipleProject}
        />
      )}
    </>
  );
};

export default UploadFileDialog;
